// UI strings — English only (owner directive 2026-07-12: the dashboard ships
// in English; the zh/es dictionaries and the language toggle were removed).

export interface Strings {
  /** Locale key — drives calendar formatting. */
  locale: 'en'

  // Brand / global
  brand: string
  loading: string
  loadError: string
  retry: string

  // Phone gate
  gateTitle: string
  gateDesc: string
  gatePlaceholder: string
  gateSubmit: string
  gateSubmitting: string
  gateRemainingAttempts: (n: number) => string
  gateVerifyFailed: string

  // Header stat tiles
  headerStreakLabel: string
  headerDaysActive: string
  headerCalorieTarget: string
  headerDefaultName: string

  // Header last-7-days strip
  headerLast7Label: string

  // Progress gauge
  progressTitleLoss: string
  progressTitleGain: string
  progressChangedLoss: string
  progressChangedGain: string
  progressToGoal: string
  progressComplete: string
  progressStartLabel: string
  progressCurrentLabel: string
  progressTargetLabel: string
  progressTitleNoGoal: string
  progressNoGoalHint: string

  // Trend chart
  trendTitle: string
  tabCombined: string
  tabWeight: string
  tabCalories: string
  trendNoData: string
  trendTargetLabel: string

  // Day log
  daylogTitle: string
  tabOverview: string
  tabDetail: string
  dayToday: string
  dayNoLog: string
  calLabel: string
  proteinLabel: string
  carbsLabel: string
  fatLabel: string
  kcalUnit: string
  /** Format a non-today YYYY-MM-DD date for the date-nav label (e.g. "Jun 15"). */
  fmtDate: (date: string) => string

  // Weekly reports section
  weeklyReportsTitle: string
  weeklyReportOpen: string

  // Calorie-target history + explainer (closed-loop controller, plan schema 2)
  targetHistoryTitle: string
  targetChartAria: string
  targetFloorLabel: string
  targetExplainerTitle: string
  /** Lead paragraph — takes the controller's lookback window in days. */
  targetExplainerBody: (windowDays: number) => string
  targetAccuracyLabel: string
  targetCoverageStat: (pct: number) => string
  targetWeighinsStat: (n: number) => string
  targetFloorNote: (kcal: number) => string
  targetFloorBindingNote: string
  /** Optional one-liner for the current phase; empty string = show nothing. */
  targetPhaseNote: (phase: string) => string
  targetUpdatedLabel: (date: string) => string

  // Exercise card
  exerciseTitle: string
  exerciseBurnedLabel: string
  exerciseNoLog: string
  exerciseDefaultActivity: string
  /** Format a duration in minutes (e.g. "30 min"). */
  fmtDuration: (min: number) => string

  // Calendar
  /** Short weekday headers in Mon-first order: [Mon, Tue, Wed, Thu, Fri, Sat, Sun] */
  calWeekdays: readonly [string, string, string, string, string, string, string]
  /** Format a YYYY-MM month key into a human title ("June 2026"). */
  fmtMonthTitle: (year: number, month: number) => string // month 1-based
  /** Calendar legend labels */
  calLegendUnder: string
  calLegendOnTarget: string
  calLegendOver: string
}

const en: Strings = {
  locale: 'en',
  brand: 'NanoRhino AI Health',
  loading: 'Loading…',
  loadError: 'Failed to load. Please refresh and try again.',
  retry: 'Retry',

  gateTitle: 'View My Progress',
  gateDesc: 'Enter the phone number you registered with to verify your identity',
  gatePlaceholder: 'Phone number',
  gateSubmit: 'Verify',
  gateSubmitting: 'Verifying…',
  gateRemainingAttempts: (n) => `(${n} attempt${n === 1 ? '' : 's'} remaining)`,
  gateVerifyFailed: 'Verification failed',

  headerStreakLabel: 'day streak',
  headerDaysActive: 'days on plan',
  headerCalorieTarget: 'cal target',
  headerDefaultName: 'User',
  headerLast7Label: 'Last 7 days',

  progressTitleLoss: 'Weight Loss Progress',
  progressTitleGain: 'Weight Gain Progress',
  progressChangedLoss: 'Total Lost',
  progressChangedGain: 'Total Gained',
  progressToGoal: 'To Goal',
  progressComplete: 'Complete',
  progressStartLabel: 'Start lb',
  progressCurrentLabel: 'Current lb',
  progressTargetLabel: 'Target lb',
  progressTitleNoGoal: 'Weight Progress',
  progressNoGoalHint: 'No goal weight set yet — text your coach a goal weight and this becomes a countdown.',

  trendTitle: 'Trends',
  tabCombined: 'Combined',
  tabWeight: 'Weight',
  tabCalories: 'Calories',
  trendNoData: 'No data yet',
  trendTargetLabel: 'Target',

  daylogTitle: 'Food Log',
  tabOverview: 'Overview',
  tabDetail: 'Detail',
  dayToday: 'Today',
  dayNoLog: 'No food logged today',
  calLabel: 'Calories',
  proteinLabel: 'Protein',
  carbsLabel: 'Carbs',
  fatLabel: 'Fat',
  kcalUnit: 'kcal',
  fmtDate: (date) => {
    // e.g. "Jun 15"
    return new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  },

  weeklyReportsTitle: 'Weekly Reports',
  weeklyReportOpen: 'Open report',

  targetHistoryTitle: 'Your Calorie Target',
  targetChartAria: 'Calorie target over time',
  targetFloorLabel: 'Floor',
  targetExplainerTitle: 'How your target is set',
  targetExplainerBody: (windowDays) =>
    `This number does not come from a textbook formula. Your coach re-reads the last ${windowDays} days ` +
    'of your own logged meals and weigh-ins, then moves the target to match how your body is actually ' +
    'responding — not how an average person is supposed to respond.',
  targetAccuracyLabel: 'What keeps it accurate',
  targetCoverageStat: (pct) => `${pct}% of days logged`,
  targetWeighinsStat: (n) => `${n} weigh-in${n === 1 ? '' : 's'}`,
  targetFloorNote: (kcal) => `Your target never drops below ${kcal} kcal.`,
  targetFloorBindingNote: 'It is sitting at that floor right now.',
  targetPhaseNote: (phase) => {
    switch (phase) {
      case 'calibrate':
        return 'Your coach is still learning your numbers, so expect the target to move a little at first.'
      case 'hold':
        return 'Your target is holding steady while your weight settles.'
      case 'maintain':
        return 'You are in maintenance — this target is set to hold your weight, not lower it.'
      case 'pause':
        return 'Your plan is paused, so the target is not moving right now.'
      default:
        return ''
    }
  },
  targetUpdatedLabel: (date) => `Last updated ${date}`,

  exerciseTitle: 'Exercise',
  exerciseBurnedLabel: 'burned',
  exerciseNoLog: 'No workouts logged',
  exerciseDefaultActivity: 'Workout',
  fmtDuration: (min) => `${Math.round(min)} min`,

  calWeekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  fmtMonthTitle: (year, month) => {
    return new Date(year, month - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  },
  calLegendUnder: 'Under',
  calLegendOnTarget: 'On Target',
  calLegendOver: 'Over',
}

export function getStrings(): Strings {
  return en
}
